import React, { useState, useEffect, useRef, useCallback } from 'react';
import { Button } from './button';
import { ImageIcon, X, Copy, Check, Download, Maximize2, Info, Loader2, Trash2, Pin, PinOff, Share2 } from 'lucide-react';
import { ShareStyleDialog } from './sharedstyles';
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from './tooltip';
import { useToast } from "./use-toast";
const API_URL = process.env.REACT_APP_API_URL ? `${process.env.REACT_APP_API_URL}/api` : '/api';

const ImageSlideshow = ({
  images,
  setImages,
  imageUrls,
  setImageUrls,
  onSettingsChange }) => {
  const [loadingImages, setLoadingImages] = useState({});
  const [loading, setLoading] = useState(false);
  const [enlargedImage, setEnlargedImage] = useState(null);
  const [copiedSettings, setCopiedSettings] = useState(null);
  const [page, setPage] = useState(0);
  const [hasMore, setHasMore] = useState(true);
  const loadingRef = useRef(null);
  const [downloadingImages, setDownloadingImages] = useState({});
  const [pinningId, setPinningId] = useState(null);
  const [shareDialogImage, setShareDialogImage] = useState(null);
  const { toast } = useToast();

  const togglePin = async (imageId, nextPinned) => {
    if (pinningId) return;
    setPinningId(imageId);
    try {
      const r = await fetch(`${API_URL}/user/images/${imageId}/pin`, {
        method: 'POST',
        headers: {
          Authorization: `Bearer ${localStorage.getItem('token')}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ pinned: nextPinned }),
      });
      if (r.status === 409) {
        toast({
          title: 'Pin limit reached',
          description: 'You can pin up to 3 maps. Unpin one to free a slot.',
          variant: 'warning',
          duration: 4000,
        });
        return;
      }
      if (!r.ok) throw new Error('Pin update failed');
      // Optimistic local update so the icon flips without a refetch.
      setImages(prev => prev.map(img => img._id === imageId ? { ...img, pinned: nextPinned } : img));
    } catch (_e) {
      toast({
        title: 'Could not update pin',
        description: 'Please try again.',
        variant: 'destructive',
        duration: 3000,
      });
    } finally {
      setPinningId(null);
    }
  };

  const IMAGES_PER_PAGE = 9;

  const loadImages = useCallback(async () => {
    if (images.length > page * IMAGES_PER_PAGE) {
      // Already loaded this page
      return;
    }
    setLoading(true);
    try {
      const response = await fetch(`${API_URL}/user/images?page=${page}&limit=${IMAGES_PER_PAGE}`, {
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('token')}`
        }
      });
      if (!response.ok) throw new Error('Failed to fetch images');
      const data = await response.json();

      setHasMore(data.images.length === IMAGES_PER_PAGE);

      const sortedImages = data.images.sort((a, b) => {
        return new Date(b.createdAt || b._id) - new Date(a.createdAt || a._id);
      });

      // Filter out duplicates using the _id field
      setImages(prev => {
        const existingIds = new Set(prev.map(img => img._id));
        const newImages = sortedImages.filter(img => !existingIds.has(img._id));
        return [...prev, ...newImages];
      });
      setLoading(false);
    } catch (error) {
      toast({
        title: "Error",
        description: "An error occured while fetching images!",
        variant: "destructive",
        duration: 3000,
      });
      setLoading(false);
    }
  }, [page, images.length, setImages]);

  // Load image preview for visible images
  const loadImagePreview = useCallback(async (imageId) => {
    // Skip if already loaded or currently loading
    if (imageUrls[imageId] || loadingImages[imageId]) return;

    setLoadingImages(prev => ({
      ...prev,
      [imageId]: true
    }));

    try {
      const response = await fetch(`${API_URL}/user/images/${imageId}/preview`, {
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('token')}`
        }
      });
      if (!response.ok) throw new Error('Failed to fetch preview');
      const blob = await response.blob();
      const url = URL.createObjectURL(blob);

      setImageUrls(prev => ({
        ...prev,
        [imageId]: url
      }));
    } catch (error) {
      toast({
        title: "Error",
        description: "An error occured while loading the preview",
        variant: "destructive",
        duration: 3000,
      });
    } finally {
      setLoadingImages(prev => ({
        ...prev,
        [imageId]: false
      }));
    }
  }, [imageUrls, loadingImages]);

  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        if (entries[0].isIntersecting && hasMore && !loading) {
          setPage(prevPage => prevPage + 1);
        }
      },
      { threshold: 0.1 }
    );

    if (loadingRef.current) {
      observer.observe(loadingRef.current);
    }

    return () => observer.disconnect();
  }, [hasMore, loading]);

  useEffect(() => {
    loadImages();
  }, [page]);

  // Cleanup URLs on unmount
  useEffect(() => {
    return () => {
      Object.values(imageUrls).forEach(url => {
        URL.revokeObjectURL(url);
      });
    };
  }, []);

  const handleDownload = async (imageId) => {
    setDownloadingImages(prev => ({ ...prev, [imageId]: true }));

    try {
      const response = await fetch(`${API_URL}/user/images/${imageId}/full`, {
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('token')}`
        }
      });

      if (!response.ok) throw new Error('Failed to download image');

      const blob = await response.blob();
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `map-${imageId}.png`;
      document.body.appendChild(a);
      a.click();
      window.URL.revokeObjectURL(url);
      document.body.removeChild(a);
    } catch (error) {
      toast({
        title: "Error",
        description: "An error occured while preparing your download",
        variant: "destructive",
        duration: 3000,
      });
    } finally {
      // Clear downloading state
      setDownloadingImages(prev => ({ ...prev, [imageId]: false }));
    }
  };

  const handleCopySettings = (image) => {
    if (image.metadata?.settings) {
      const relevantSettings = {
        activities: image.metadata.settings.activities,
        buildings: image.metadata.settings.buildings,
        forests: image.metadata.settings.forests,
        naturalareas: image.metadata.settings.naturalareas,
        water: image.metadata.settings.water,
        oceans: image.metadata.settings.oceans,
        streets: image.metadata.settings.streets,
        land: image.metadata.settings.land
      };

      onSettingsChange(relevantSettings);
      setCopiedSettings(image._id);
      setTimeout(() => setCopiedSettings(null), 2000);
    }
  };

  const handleEnlargeImage = (e, image) => {
    e.stopPropagation();
    setEnlargedImage(image);
  };

  const handleCloseEnlarged = () => {
    setEnlargedImage(null);
  };

  const ImageCard = React.memo(({ image }) => {
    const imageRef = useRef(null);

    useEffect(() => {
      const observer = new IntersectionObserver(
        (entries) => {
          if (entries[0].isIntersecting) {
            loadImagePreview(image._id);
          }
        },
        {
          threshold: 0.1,
          rootMargin: '50px' // Preload images slightly before they come into view
        }
      );

      const currentRef = imageRef.current;
      if (currentRef) {
        observer.observe(currentRef);
      }

      return () => {
        if (currentRef) {
          observer.unobserve(currentRef);
        }
        observer.disconnect();
      };
    }, [image._id, loadImagePreview]);

    return (
      <div
        ref={imageRef}
        className="aspect-[4/3] relative group cursor-pointer"
      >
        {imageUrls[image._id] ? (
          <img
            src={imageUrls[image._id]}
            alt={image.metadata?.place_name || 'Map preview'}
            className="w-full h-full object-cover rounded-xl shadow-2xl"
          />
        ) : (
          <div className="w-full h-full bg-gray-100 rounded-lg flex items-center justify-center">
            <ImageIcon className="h-4 w-4 lg:h-8 lg:w-8 text-gray-400" />
          </div>
        )}

        {/* Top overlay for place name */}
        {/* <div className="absolute top-0 bg-red-500 text-white p-0 lg:p-2 rounded-t-lg 
                        group-hover:bg-opacity-70 transition-colors">
          <p className="text-sm lg:text-lg truncate">
            {image.metadata?.place_name || 'Unnamed location'}
          </p>
        </div> */}

        {/* Bottom overlay for buttons */}
        <div className="absolute bottom-0 left-0 right-0 bg-black bg-opacity-50 text-white p-1 lg:p-2 rounded-b-xl 
                        group-hover:bg-opacity-70 transition-colors">
          <div className="flex justify-between items-center">
            <TooltipProvider>
              <Tooltip>
                <TooltipTrigger asChild>
                  <Button
                    variant="ghost"
                    size="icon"
                    className="h-4 w-4 lg:h-8 lg:w-8 text-white hover:text-blue-400"
                    onClick={(e) => {
                      e.stopPropagation();
                      handleCopySettings(image);
                    }}
                  >
                    {copiedSettings === image._id ? (
                      <Check className="h-4 w-4 lg:h-5 lg:w-5" />
                    ) : (
                      <Copy className="h-4 w-4 lg:h-5 lg:w-5" />
                    )}
                  </Button>
                </TooltipTrigger>
                <TooltipContent>
                  <p>Copy settings from this image</p>
                </TooltipContent>
              </Tooltip>
            </TooltipProvider>

            <TooltipProvider>
              <Tooltip>
                <TooltipTrigger asChild>
                  <Button
                    variant="ghost"
                    size="icon"
                    className="h-4 w-4 lg:h-8 lg:w-8 text-white hover:text-blue-400"
                    onClick={(e) => {
                      e.stopPropagation();
                      handleDownload(image._id);
                    }}
                    disabled={downloadingImages[image._id]}
                  >
                    {downloadingImages[image._id] ? (
                      <Loader2 className="h-4 w-4 lg:h-5 lg:w-5 animate-spin" />
                    ) : (
                      <Download className="h-4 w-4 lg:h-5 lg:w-5" />
                    )}
                  </Button>
                </TooltipTrigger>
                <TooltipContent>
                  <p>{downloadingImages[image._id] ? 'Downloading...' : 'Download full image'}</p>
                </TooltipContent>
              </Tooltip>
            </TooltipProvider>

            <TooltipProvider>
              <Tooltip>
                <TooltipTrigger asChild>
                  <Button
                    variant="ghost"
                    size="icon"
                    className="h-4 w-4 lg:h-8 lg:w-8 text-white hover:text-blue-400"
                    onClick={(e) => handleEnlargeImage(e, {
                      name: image.metadata?.place_name || 'Unnamed location',
                      imageUrl: imageUrls[image._id]
                    })}
                  >
                    <Maximize2 className="h-4 w-4 lg:h-5 lg:w-5" />
                  </Button>
                </TooltipTrigger>
                <TooltipContent>
                  <p>View larger image</p>
                </TooltipContent>
              </Tooltip>
            </TooltipProvider>

            <TooltipProvider>
              <Tooltip>
                <TooltipTrigger asChild>
                  <Button
                    variant="ghost"
                    size="icon"
                    disabled={pinningId === image._id}
                    className={`h-4 w-4 lg:h-8 lg:w-8 hover:text-yellow-300 ${image.pinned ? 'text-yellow-300' : 'text-white'}`}
                    onClick={(e) => {
                      e.stopPropagation();
                      togglePin(image._id, !image.pinned);
                    }}
                  >
                    {image.pinned
                      ? <PinOff className="h-4 w-4 lg:h-5 lg:w-5" />
                      : <Pin className="h-4 w-4 lg:h-5 lg:w-5" />
                    }
                  </Button>
                </TooltipTrigger>
                <TooltipContent>
                  <p>{image.pinned ? 'Unpin from preview tab' : 'Pin to preview tab'}</p>
                </TooltipContent>
              </Tooltip>
            </TooltipProvider>

            {image.metadata?.settings?.renderMode !== 'basic' && (
              <TooltipProvider>
                <Tooltip>
                  <TooltipTrigger asChild>
                    <Button
                      variant="ghost"
                      size="icon"
                      className="h-4 w-4 lg:h-8 lg:w-8 text-white hover:text-blue-400"
                      onClick={(e) => {
                        e.stopPropagation();
                        setShareDialogImage(image);
                      }}
                    >
                      <Share2 className="h-4 w-4 lg:h-5 lg:w-5" />
                    </Button>
                  </TooltipTrigger>
                  <TooltipContent>
                    <p>Share this style</p>
                  </TooltipContent>
                </Tooltip>
              </TooltipProvider>
            )}

            <TooltipProvider>
              <Tooltip>
                <TooltipTrigger asChild>
                  <Button
                    variant="ghost"
                    size="icon"
                    className="h-4 w-4 lg:h-8 lg:w-8 text-white hover:text-blue-400"
                  >
                    <Info className="h-4 w-4 lg:h-5 lg:w-5" />
                  </Button>
                </TooltipTrigger>
                <TooltipContent>
                  <div className="text-xs lg:text-sm space-y-1">
                    <p>{image.metadata?.place_name || 'Unnamed location'}</p>
                    <p>Format: {image.metadata?.format?.value || 'N/A'}</p>
                    <p>Printable size: {image.metadata?.width || '0'}x{image.metadata?.height || '0'} cm</p>
                    <p>DPI: {image.metadata?.dpi || '300'}</p>
                  </div>
                </TooltipContent>
              </Tooltip>
            </TooltipProvider>
          </div>
        </div>
      </div>
    );
  });

  if (loading && images.length === 0) {
    return (
      <div className="h-48 flex items-center justify-center">
        <div className="animate-spin">
          <ImageIcon className="h-8 w-8 text-gray-400" />
        </div>
      </div>
    );
  }

  return (
    <div className="flex flex-col h-full">
      <div className="flex items-center justify-center text-xs lg:text-xl font-medium py-2">
        Download your images or copy their settings for your next image
      </div>
      <div className="flex-1 overflow-hidden">
        <div className="grid grid-cols-2 gap-2 lg:grid-cols-3 lg:gap-4 w-full h-full overflow-y-auto p-2">
          {images.map((image) => (
            <ImageCard key={image._id} image={image} />
          ))}
          {hasMore && (
            <div ref={loadingRef} className="col-span-2 lg:col-span-3 flex justify-center p-4">
              <div className="animate-spin">
                <ImageIcon className="h-4 w-4 lg:h-6 lg:w-6 text-gray-400" />
              </div>
            </div>
          )}
        </div>
      </div>
      {!hasMore && images.length === 0 && (
        <div className="text-center text-gray-500 py-8">
          <ImageIcon className="h-4 w-4 lg:h-8 lg:w-8 mx-auto mb-2" />
          <p>No maps available</p>
        </div>
      )}
      {shareDialogImage && (
        <ShareStyleDialog
          open={!!shareDialogImage}
          onOpenChange={(o) => { if (!o) setShareDialogImage(null); }}
          settings={shareDialogImage.metadata?.settings || {}}
          source={{ kind: 'image', id: shareDialogImage._id }}
          defaultStyleName={shareDialogImage.metadata?.place_name || ''}
        />
      )}
      {enlargedImage && (
        <div
          className="fixed inset-0 bg-black backdrop-blur-sm bg-opacity-50 flex items-center justify-center z-[9999] p-4"
          onClick={handleCloseEnlarged}
        >
          <div
            className="bg-white rounded-xl w-full max-w-3xl relative"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="flex justify-between items-center p-4 border-b">
              <h3 className="text-lg font-semibold">{enlargedImage.name}</h3>
              <Button
                variant="ghost"
                size="icon"
                onClick={handleCloseEnlarged}
                className="text-gray-500 hover:text-gray-700"
              >
                <X className="h-6 w-6" />
              </Button>
            </div>
            <div className="p-6">
              <img
                src={enlargedImage.imageUrl}
                alt={`Enlarged preview for ${enlargedImage.name}`}
                className="w-full h-auto rounded-lg shadow-2xl shadow-black/50"
              />
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

const AdminImageSlideshow = ({ userEmail }) => {
  const [images, setImages] = useState([]);
  const [imageUrls, setImageUrls] = useState({});
  const [loadingImages, setLoadingImages] = useState({});
  const [loading, setLoading] = useState(true);
  const [enlargedImage, setEnlargedImage] = useState(null);
  const [page, setPage] = useState(0);
  const [hasMore, setHasMore] = useState(true);
  const loadingRef = useRef(null);
  const [downloadingImages, setDownloadingImages] = useState({});
  const [selectedImage, setSelectedImage] = useState(null);
  const [deleting, setDeleting] = useState(false);
  const { toast } = useToast();

  const IMAGES_PER_PAGE = 9;

  const loadImages = useCallback(async () => {
    if (images.length > page * IMAGES_PER_PAGE) {
      return;
    }

    try {
      const response = await fetch(
        `${API_URL}/admin/users/${encodeURIComponent(userEmail)}/images?page=${page}&limit=${IMAGES_PER_PAGE}`,
        {
          headers: {
            'Authorization': `Bearer ${localStorage.getItem('token')}`
          }
        }
      );

      if (!response.ok) throw new Error('Failed to fetch images');
      const data = await response.json();

      setHasMore(data.images.length === IMAGES_PER_PAGE);

      const sortedImages = data.images.sort((a, b) => {
        return new Date(b.createdAt || b._id) - new Date(a.createdAt || a._id);
      });

      setImages(prev => {
        const existingIds = new Set(prev.map(img => img._id));
        const newImages = sortedImages.filter(img => !existingIds.has(img._id));
        return [...prev, ...newImages];
      });
      setLoading(false);
    } catch (error) {
      toast({
        title: "Error",
        description: "An error occured fetching images",
        variant: "destructive",
        duration: 3000,
      });
      setLoading(false);
    }
  }, [page, images.length, userEmail]);

  const loadImagePreview = useCallback(async (imageId) => {
    if (imageUrls[imageId] || loadingImages[imageId]) return;

    setLoadingImages(prev => ({
      ...prev,
      [imageId]: true
    }));

    try {
      const response = await fetch(
        `${API_URL}/admin/users/${encodeURIComponent(userEmail)}/images/${imageId}/preview`,
        {
          headers: {
            'Authorization': `Bearer ${localStorage.getItem('token')}`
          }
        }
      );

      if (!response.ok) throw new Error('Failed to fetch preview');
      const blob = await response.blob();
      const url = URL.createObjectURL(blob);

      setImageUrls(prev => ({
        ...prev,
        [imageId]: url
      }));
    } catch (error) {

      toast({
        title: "Error",
        description: "An error occured loading the preview",
        variant: "destructive",
        duration: 3000,
      });
    } finally {
      setLoadingImages(prev => ({
        ...prev,
        [imageId]: false
      }));
    }
  }, [imageUrls, loadingImages, userEmail]);

  const handleDownload = async (imageId) => {
    setDownloadingImages(prev => ({ ...prev, [imageId]: true }));

    try {
      const response = await fetch(
        `${API_URL}/admin/users/${encodeURIComponent(userEmail)}/images/${imageId}/full`,
        {
          headers: {
            'Authorization': `Bearer ${localStorage.getItem('token')}`
          }
        }
      );

      if (!response.ok) throw new Error('Failed to download image');

      const blob = await response.blob();
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `map-${imageId}.png`;
      document.body.appendChild(a);
      a.click();
      window.URL.revokeObjectURL(url);
      document.body.removeChild(a);
    } catch (error) {

      toast({
        title: "Error",
        description: "An error occured while downloading your image",
        variant: "destructive",
        duration: 3000,
      });
    } finally {
      setDownloadingImages(prev => ({ ...prev, [imageId]: false }));
    }
  };

  const handleDelete = async () => {
    if (!selectedImage) return;

    setDeleting(true);
    try {
      const response = await fetch(
        `${API_URL}/admin/users/${encodeURIComponent(userEmail)}/images/${selectedImage._id}`,
        {
          method: 'DELETE',
          headers: {
            'Authorization': `Bearer ${localStorage.getItem('token')}`
          }
        }
      );

      if (!response.ok) throw new Error('Failed to delete image');

      // Remove the image from the list
      setImages(prev => prev.filter(img => img._id !== selectedImage._id));
      // Clean up the URL
      if (imageUrls[selectedImage._id]) {
        URL.revokeObjectURL(imageUrls[selectedImage._id]);
        setImageUrls(prev => {
          const newUrls = { ...prev };
          delete newUrls[selectedImage._id];
          return newUrls;
        });
      }
      setSelectedImage(null);
    } catch (error) {

      toast({
        title: "Error",
        description: "An error occured while deleting your image",
        variant: "destructive",
        duration: 3000,
      });
    } finally {
      setDeleting(false);
    }
  };

  const handleEnlargeImage = (e, image) => {
    e.stopPropagation();
    setEnlargedImage(image);
  };

  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        if (entries[0].isIntersecting && hasMore && !loading) {
          setPage(prevPage => prevPage + 1);
        }
      },
      { threshold: 0.1 }
    );

    if (loadingRef.current) {
      observer.observe(loadingRef.current);
    }

    return () => observer.disconnect();
  }, [hasMore, loading]);

  useEffect(() => {
    loadImages();
  }, [loadImages, page]);

  useEffect(() => {
    return () => {
      Object.values(imageUrls).forEach(url => {
        URL.revokeObjectURL(url);
      });
    };
  }, [imageUrls]);

  // Reset everything when userEmail changes
  useEffect(() => {
    setPage(0);
    setImages([]);
    setImageUrls({});
    setHasMore(true);
    setLoading(true);
    setSelectedImage(null);
  }, [userEmail]);

  const handleCloseEnlarged = () => {
    setEnlargedImage(null);
  };

  const ImageCard = React.memo(({ image }) => {
    const imageRef = useRef(null);
    const isSelected = selectedImage?._id === image._id;

    useEffect(() => {
      const observer = new IntersectionObserver(
        (entries) => {
          if (entries[0].isIntersecting) {
            loadImagePreview(image._id);
          }
        },
        {
          threshold: 0.1,
          rootMargin: '50px'
        }
      );

      const currentRef = imageRef.current;
      if (currentRef) {
        observer.observe(currentRef);
      }

      return () => {
        if (currentRef) {
          observer.unobserve(currentRef);
        }
        observer.disconnect();
      };
    }, [image._id]);

    return (
      <div
        ref={imageRef}
        className={`aspect-[4/3] relative group cursor-pointer ${isSelected ? 'ring-2 ring-red-500' : ''
          }`}
        onClick={() => setSelectedImage(isSelected ? null : image)}
      >
        {imageUrls[image._id] ? (
          <img
            src={imageUrls[image._id]}
            alt={image.metadata?.place_name || 'Map preview'}
            className="w-full h-full object-cover rounded-lg border border-gray-200"
          />
        ) : (
          <div className="w-full h-full bg-gray-100 rounded-lg flex items-center justify-center">
            <ImageIcon className="h-4 w-4 lg:h-8 lg:w-8 text-gray-400" />
          </div>
        )}
        <div className="absolute bottom-0 left-0 right-0 bg-black bg-opacity-50 text-white p-1 lg:p-2 rounded-b-lg 
                      group-hover:bg-opacity-70 transition-colors">
          <div className="flex justify-between items-center">
            <p className="text-xs lg:text-sm truncate">
              {image.metadata?.place_name || 'Unnamed location'}
            </p>
            <div className="flex space-x-1">
              <TooltipProvider>
                <Tooltip>
                  <TooltipTrigger asChild>
                    <Button
                      variant="ghost"
                      size="icon"
                      className="h-4 w-4 lg:h-8 lg:w-8 text-white hover:text-blue-400"
                      onClick={(e) => {
                        e.stopPropagation();
                        handleDownload(image._id);
                      }}
                      disabled={downloadingImages[image._id]}
                    >
                      {downloadingImages[image._id] ? (
                        <Loader2 className="h-3 w-3 lg:h-4 lg:w-4 animate-spin" />
                      ) : (
                        <Download className="h-3 w-3 lg:h-4 lg:w-4" />
                      )}
                    </Button>
                  </TooltipTrigger>
                  <TooltipContent>
                    <p>{downloadingImages[image._id] ? 'Downloading...' : 'Download full image'}</p>
                  </TooltipContent>
                </Tooltip>
              </TooltipProvider>

              <TooltipProvider>
                <Tooltip>
                  <TooltipTrigger asChild>
                    <Button
                      variant="ghost"
                      size="icon"
                      className="h-4 w-4 lg:h-8 lg:w-8 text-white hover:text-blue-400"
                      onClick={(e) => handleEnlargeImage(e, {
                        name: image.metadata?.place_name || 'Unnamed location',
                        imageUrl: imageUrls[image._id]
                      })}
                    >
                      <Maximize2 className="h-3 w-3 lg:h-4 lg:w-4" />
                    </Button>
                  </TooltipTrigger>
                  <TooltipContent>
                    <p>View larger image</p>
                  </TooltipContent>
                </Tooltip>
              </TooltipProvider>

              <TooltipProvider>
                <Tooltip>
                  <TooltipTrigger asChild>
                    <Button
                      variant="ghost"
                      size="icon"
                      className="h-4 w-4 lg:h-8 lg:w-8 text-white hover:text-blue-400"
                    >
                      <Info className="h-3 w-3 lg:h-4 lg:w-4" />
                    </Button>
                  </TooltipTrigger>
                  <TooltipContent>
                    <div className="text-xs lg:text-sm space-y-1">
                      <p>Format: {image.metadata?.format?.value || 'N/A'}</p>
                      <p>Printable size: {image.metadata?.width || '0'}x{image.metadata?.height || '0'} cm</p>
                      <p>DPI: {image.metadata?.dpi || '300'}</p>
                    </div>
                  </TooltipContent>
                </Tooltip>
              </TooltipProvider>
            </div>
          </div>
        </div>
      </div>
    );
  });

  if (loading && images.length === 0) {
    return (
      <div className="h-48 flex items-center justify-center">
        <div className="animate-spin">
          <ImageIcon className="h-8 w-8 text-gray-400" />
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <div className="text-xs lg:text-xl font-medium">
          Images for {userEmail}
        </div>
        <Button
          variant="destructive"
          size="sm"
          className="flex items-center gap-2"
          disabled={!selectedImage || deleting}
          onClick={handleDelete}
        >
          {deleting ? (
            <Loader2 className="h-4 w-4 animate-spin" />
          ) : (
            <Trash2 className="h-4 w-4" />
          )}
          Delete Selected
        </Button>
      </div>

      <div className="grid grid-cols-2 gap-2 lg:grid-cols-3 lg:gap-4 w-full max-h-[calc(1.3*20rem)] lg:max-h-[calc(2.5*20rem)] overflow-y-auto p-2">
        {images.map((image) => (
          <ImageCard key={image._id} image={image} />
        ))}
        {hasMore && (
          <div ref={loadingRef} className="col-span-2 lg:col-span-3 flex justify-center p-4">
            <div className="animate-spin">
              <ImageIcon className="h-4 w-4 lg:h-6 lg:w-6 text-gray-400" />
            </div>
          </div>
        )}
      </div>

      {!hasMore && images.length === 0 && (
        <div className="text-center text-gray-500">
          <ImageIcon className="h-4 w-4 lg:h-8 lg:w-8 mx-auto mb-2" />
          <p>No maps available</p>
        </div>
      )}
      {enlargedImage && (
        <div
          className="fixed inset-0 bg-black backdrop-blur-sm bg-opacity-50 flex items-center justify-center z-[9999] p-4"
          onClick={handleCloseEnlarged}
        >
          <div
            className="bg-white rounded-xl w-full max-w-3xl relative"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="flex justify-between items-center p-4 border-b">
              <h3 className="text-lg font-semibold">{enlargedImage.name}</h3>
              <Button
                variant="ghost"
                size="icon"
                onClick={handleCloseEnlarged}
                className="text-gray-500 hover:text-gray-700"
              >
                <X className="h-6 w-6" />
              </Button>
            </div>
            <div className="p-6">
              <img
                src={enlargedImage.imageUrl}
                alt={`Enlarged preview for ${enlargedImage.name}`}
                className="w-full h-auto rounded-lg shadow-2xl shadow-black/50"
              />
            </div>
          </div>
        </div>
      )}
    </div>
  );
};


export { AdminImageSlideshow, ImageSlideshow };